import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import DescriptionList from 'tio-ui/components/description-list';

interface EmployeeSummarySignature {
  Args: {
    /** Missing counts are unavailable, not zero. */
    totalAccounts?: number | null;
    totalLoans?: number | null;
    /** Participation in the benefit, distinct from the account's active state. */
    participationStatus?: string | null;
    plan?: { companyKey?: string | null; name?: string | null } | null;
    /** Opens the existing company plan destination, including when plan metadata is absent. */
    onViewPlans: () => void;
  };
}

export default class EmployeeSummary extends Component<EmployeeSummarySignature> {
  @service declare intl: IntlService;

  #formatCount(value: number | null | undefined) {
    return typeof value === 'number' && Number.isFinite(value)
      ? this.intl.formatNumber(value)
      : this.intl.t('partner.employee.summary.unavailable');
  }

  get totalAccounts() {
    return this.#formatCount(this.args.totalAccounts);
  }

  get totalLoans() {
    return this.#formatCount(this.args.totalLoans);
  }

  get participationLabel() {
    const status = this.args.participationStatus?.trim();
    if (!status) return this.intl.t('partner.employee.summary.unavailable');
    if (['participating', 'pending', 'deactivated'].includes(status)) {
      return this.intl.t(`partner.employee.${status}`);
    }
    return status;
  }

  get planLabel() {
    return [this.args.plan?.companyKey?.trim(), this.args.plan?.name?.trim()]
      .filter(Boolean)
      .join(' — ');
  }

  <template>
    <Card @padding="sm" @elevation="none" data-test-employee-summary>
      <DescriptionList @layout="summary" aria-label={{t "partner.employee.summary.label"}} as |dl|>
        <dl.Group data-test-employee-summary-accounts>
          <dl.Term>{{t "partner.employee.accounts"}}</dl.Term>
          <dl.Details>{{this.totalAccounts}}</dl.Details>
        </dl.Group>
        <dl.Group data-test-employee-summary-loans>
          <dl.Term>{{t "partner.employee.loans"}}</dl.Term>
          <dl.Details>{{this.totalLoans}}</dl.Details>
        </dl.Group>
        <dl.Group data-test-employee-summary-participation>
          <dl.Term>{{t "partner.employee.summary.participation"}}</dl.Term>
          <dl.Details>{{this.participationLabel}}</dl.Details>
        </dl.Group>
        <dl.Group data-test-employee-summary-plan>
          <dl.Term>{{t "partner.employee.plan"}}</dl.Term>
          <dl.Details>
            <Button
              @appearance="link"
              @intent="primary"
              @size="sm"
              @testHook="employee-summary__view-plans"
              @ariaLabel={{if
                this.planLabel
                (concat (t "partner.employee.plan_details") ": " this.planLabel)
                (t "partner.employee.plan_details")
              }}
              class="min-h-12 max-w-full whitespace-normal text-left"
              {{on "click" @onViewPlans}}
            >{{if this.planLabel this.planLabel (t "partner.employee.plan_details")}}</Button>
          </dl.Details>
        </dl.Group>
      </DescriptionList>
    </Card>
  </template>
}
